import { useEffect, useMemo, useState } from 'react';
import { Divider, Table, Typography, notification } from 'antd';

import { axiosGet } from '../../services/http.services';
import { API_ALLAIRPORT, API_GET_DATA, API_UNIQUE_AP_AL } from '../../api/api';
import CustomButton from '../../customs/CustomButton';
import { exportRowsToExcel } from '../../utils/dashboard';


const { Title } = Typography;
const html2pdf = require('html2pdf.js');

const preferredAirlineOrder = [
  'Indigo',
  'Alliance Air',
  'Air India',
  'Air Asia',
  'Fly 91',
  'Air India Exp',
  'Akasa Air',
  'Star Air',
  'SpiceJet',
  'Vistara',
  'Go First',
];

const formatAirlineHeader = (value) => String(value || '').replace(/\s+/g, '').toUpperCase();
const formatAirlinePillLabel = (value) => String(value || '').replace(/\s+/g, '');
const normalizeCityName = (value) => String(value || '').replace(/\s*\([A-Z]{3,4}\)\s*$/, '').trim();

const coveragePillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 28,
  padding: '3px 10px',
  borderRadius: 4,
  background: '#52c41a',
  color: '#fff',
  fontSize: 12,
  fontWeight: "bold",
  lineHeight: 1.4,
};

const exportCoveragePillStyle = {
  ...coveragePillStyle,
  background: 'transparent',
  border: 'none',
  color: '#000',
};

const emptyCoveragePillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 0,
  borderRadius: 4,
  border: '1px solid #52c41a',
  background: '#fff',
};


const ActiveAirlinesSection = () => {
  const [airlineOptions, setAirlineOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [isDownloadPdf, setIsDownloadPdf] = useState(false);

  const exportRows = useMemo(
    () =>
      rows.map((row) => ({
        Airport: row.airport,
        CITY: row.city || '-',
        ...Object.fromEntries(airlineOptions.map((airline) => [airline, row[airline] || '-'])),
      })),
    [airlineOptions, rows]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [metaResponse, matrixResponse, airportResponse] = await Promise.all([
          axiosGet(API_GET_DATA),
          axiosGet(API_UNIQUE_AP_AL),
          axiosGet(API_ALLAIRPORT),
        ]);

        const activeAirlines = (metaResponse?.data?.airlines || []).map((item) => item?.value).filter(Boolean);
        const orderedAirlines = [
          ...preferredAirlineOrder.filter((airline) => activeAirlines.includes(airline)),
          ...activeAirlines
            .filter((airline) => !preferredAirlineOrder.includes(airline))
            .sort((left, right) => left.localeCompare(right)),
        ];
        setAirlineOptions(orderedAirlines);

        const airportMap = Object.fromEntries(
          (airportResponse?.data || [])
            .map((row) => {
              const code = String(row?.[0] || '').trim().toUpperCase();
              const city = String(row?.[1] || '').trim();
              if (!code) {
                return null;
              }
              return [code, normalizeCityName(city)];
            })
            .filter(Boolean)
        );
        const matrixRows = (matrixResponse?.data || []).map((row) => {
          const airlineSet = new Set((row || []).slice(1).filter(Boolean));
          const airportCode = String(row[0] || '').trim().toUpperCase();
          const item = {
            airport: airportCode,
            city: airportMap[airportCode] || '',
          };
          orderedAirlines.forEach((airline) => {
            item[airline] = airlineSet.has(airline) ? airline : null;
          });
          item.coverageCount = orderedAirlines.reduce((count, airline) => count + (item[airline] ? 1 : 0), 0);
          return item;
        })
          .sort((left, right) => left.airport.localeCompare(right.airport));
        setRows(matrixRows);
      } catch (error) {
        notification.error({
          message: 'Unable to Load Airport Airline Matrix',
          description: 'Active airline coverage could not be loaded.',
        });
      }
    };

    load();
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Airport',
        dataIndex: 'airport',
        key: 'airport',
        align: 'center',
        render: (value) => (
          <p className='text-center' style={{ marginBottom: 0 }}>
            <strong>{value}</strong>
          </p>
        ),
      },
      {
        title: 'CITY',
        dataIndex: 'city',
        key: 'city',
        align: 'center',
        render: (value) => (
          <p className='text-center' style={{ marginBottom: 0 }}>
            <strong>{value || '-'}</strong>
          </p>
        ),
      },
      ...airlineOptions.map((airline) => ({
        title: formatAirlineHeader(airline),
        dataIndex: airline,
        key: airline,
        align: 'center',
        render: (value) => (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {value ? (
              <span style={isDownloadPdf ? exportCoveragePillStyle : coveragePillStyle}>
                {formatAirlinePillLabel(value)}
              </span>
            ) : (
              <span style={emptyCoveragePillStyle} aria-label="No airline coverage" />
            )}
          </div>
        ),
      })),
    ],
    [airlineOptions, isDownloadPdf]
  );

  const downloadPdf = async () => {
    const element = document.getElementById('UniqueAPAL');
    if (!element) {
      return;
    }

    setIsDownloadPdf(true);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const contentWidth = element.offsetWidth;
    const contentHeight = element.offsetHeight;
    const scaleFactor = 0.3;
    const pdfWidth = contentWidth * scaleFactor;
    const pdfHeight = contentHeight * scaleFactor;

    try {
      await html2pdf()
        .from(element)
        .set({
          image: { type: 'jpeg', quality: 1 },
          html2canvas: { scale: 3, letterRendering: true, logging: false },
          jsPDF: { unit: 'mm', format: [pdfWidth, pdfHeight], orientation: 'landscape' },
        })
        .save('Active-Airlines-in-Airports.pdf');
    } finally {
      setIsDownloadPdf(false);
    }
  };

  return (
    <div id="UniqueAPAL" style={{ position: 'relative', marginTop: 50 }}>
      <div style={{ position: 'relative' }}>
        <Title level={4} style={{ borderRadius: '5px 25px', padding: '5px 25px', textAlign: 'center' }}>
        Active Airlines in Airports
        </Title>

        {!isDownloadPdf ? (
          <div style={{ position: 'absolute', top: 7, right: 30 }}>
            <CustomButton
              className="ml-2"
              icon={<img src={require('../../assests/download.png')} alt="download" loading="lazy" style={{ width: 15, height: 15 }} />}
              label="PDF"
              onClick={downloadPdf}
            />

            <CustomButton
              className="ml-2"
              icon={<img src={require('../../assests/download.png')} alt="download" loading="lazy" style={{ width: 15, height: 15 }} />}
              label="XLSX"
              onClick={() => exportRowsToExcel(exportRows, 'active-airlines-in-airports', 'AP-AL')}
            />
          </div>
        ) : null}
      </div>

      <Divider style={{ margin: 20, marginBottom: 0 }} />

      <div
        style={{
          marginTop: 30,
          boxShadow: '0 1px 5px rgba(0, 0, 0, 0.2)',
          borderRadius: 15,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="airport"
          pagination={false}
          scroll={{ x: true, align: 'center' }}
          size="middle"
        />
      </div>
    </div>
  );
};


export default ActiveAirlinesSection;
